
import { Component, Vue, Prop } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import { PurchaseOrders } from '@/collections/purchase_orders';
import { PurchaseOrderModel } from '@/models/purchase_order';
import POPaymentStatusBadge from '@/components/purchase-order/POPaymentStatusBadge.vue';
import { SupplierList } from '@/collections/suppliers';
import { SupplierModel } from '@/models/supplier';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import dateFormat from 'dateformat';
import { EventBus } from '@/main';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { ax, getIdentityToken, extractApiErrorMessage } from '@/services/http';
import { PO_SEARCH_PLACEHOLDER } from '@/views/purchase-order/purchaseOrderSearchConfig';
import {
  parsePoListQuery,
  serializePoListState,
  queryShallowEqual,
  PoListQueryState,
  PoSearchFieldValue,
  PO_LIST_DEFAULTS,
  PO_SEARCH_FIELD_VALUES,
  PO_SEARCH_FIELD_LABELS,
  PO_STATUS_PRESETS,
  normalizePoStatuses,
  findPoStatusPreset,
} from '@/views/purchase-order/purchaseOrderListQuery';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import { getFeatureVersion, setFeatureVersion, purchaseOrderSwitchTarget } from '@/config/atlasFeatures';
import ManageColumnsMenu from '@/components/purchase-order/ManageColumnsMenu.vue';
import {
  filterHeaders,
  fetchListColumnsConfig,
  saveListColumnsConfig,
  DEFAULT_VISIBLE_COLUMNS,
  TOGGLEABLE_COLUMN_LABELS,
} from '@/views/purchase-order/poListColumns';

@Component({
  components: {
    CopyLinkButton,
    POPaymentStatusBadge,
    TryNewAtlasBanner,
    ManageColumnsMenu,
  },
})
export default class PurchaseOrderList extends Vue {
  @Prop({ default: () => new StoreModel() }) store!: StoreModel;

  private purchaseOrders: any[] = [];
  private totalPurchaseOrders = 0;
  private loading = false;
  // Exposed for template. See purchaseOrderSearchConfig.ts.
  private poSearchPlaceholder = PO_SEARCH_PLACEHOLDER;
  private search = '';
  private searchField: PoSearchFieldValue | '' = '';
  private filters = {
    status: [] as string[],
    supplierId: null as number | null,
    locationId: null as number | null,
    source: '',
    orderDateFrom: '',
    orderDateTo: '',
    departureDateFrom: '',
    departureDateTo: '',
    expectedDeliveryDateFrom: '',
    expectedDeliveryDateTo: '',
    paymentStatus: [] as string[],
    overdue: false
  };
  private paymentStatusOptions = [
    { text: 'Unpaid', value: 'unpaid' },
    { text: 'Partial', value: 'partial' },
    { text: 'Paid', value: 'paid' }
  ];
  private options = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ['orderDate'],
    sortDesc: [true]
  };
  private headers = [
    { text: 'PO Number', value: 'poNumber', width: '140px' },
    { text: 'Supplier', value: 'supplierName', width: '180px' },
    { text: 'Delivery Location', value: 'locationName', width: '160px' },
    { text: 'Source', value: 'source', width: '140px' },
    { text: 'Status', value: 'status', width: '160px' },
    { text: 'Order Date', value: 'orderDate', width: '140px' },
    { text: 'Departure', value: 'departureDate', width: '140px' },
    { text: 'Expected Delivery', value: 'expectedDeliveryDate', width: '160px' },
    { text: 'Total', value: 'totalAmount', width: '120px' },
    { text: 'Subtotal', value: 'subtotal', width: '120px' },
    { text: 'Item Tax', value: 'itemTax', width: '120px' },
    { text: 'Shipping', value: 'shippingCost', width: '120px' },
    { text: 'Shipping Tax', value: 'shippingTax', width: '120px' },
    { text: 'Discount', value: 'discount', width: '120px' },
    { text: 'Adjustment', value: 'adjustment', width: '120px' },
    { text: 'Total Tax', value: 'taxTotal', width: '120px' },
    { text: 'Grand Total', value: 'grandTotal', width: '140px' },
    { text: 'Payment', value: 'paymentStatus', width: '130px' },
    { text: 'Payment Due', value: 'paymentDueDate', width: '140px' },
    { text: 'Invoice Number', value: 'invoiceNumber', width: '160px' },
    { text: 'Internal Note', value: 'internalNotes', width: '200px' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '100px' }
  ];
  private visibleColumns: string[] = DEFAULT_VISIBLE_COLUMNS;
  private toggleableColumnLabels = TOGGLEABLE_COLUMN_LABELS;
  // Tracks the latest column-config operation (the initial load, or an
  // onApplyColumns save). An operation only applies its result when it is
  // still the latest one — otherwise a slow, stale operation (the initial
  // load resolving after a local change, or an earlier save resolving after
  // a later one) could overwrite a newer selection.
  private columnConfigOpVersion = 0;

  get filteredHeaders() {
    return filterHeaders(this.headers, this.visibleColumns);
  }
  private purchaseOrdersCollection = new PurchaseOrders();
  private suppliers: SupplierModel[] = [];
  private locations: StoreLocationModel[] = [];
  private loadingSuppliers = false;
  private loadingLocations = false;

  // Dialog states
  private createPODialogVisible = false;
  private selectedSupplierId: number | null = null;

  // Import PO dialog
  private showCreateForImportDialog = false;
  private creatingForImport = false;
  private importFormValid = false;
  private importDateMenu = false;
  private importPO = {
    supplierId: null as number | null,
    locationId: null as number | null,
    expectedDeliveryDate: '',
    notes: ''
  };

  // Export dialog
  private showExportDialog = false;
  private exportFormat: 'csv' | 'xlsx' | 'pdf' = 'csv';
  private exporting = false;
  private exportIncludeFinancials = true;

  // Filter panel
  private filterPanelExpanded: number | undefined = 0;

  // Date filter menus
  private orderDateFromMenu = false;
  private orderDateToMenu = false;
  private departureDateFromMenu = false;
  private departureDateToMenu = false;
  private expectedDeliveryDateFromMenu = false;
  private expectedDeliveryDateToMenu = false;

  // Debounced search handler
  private debouncedSearch: any = null;

  // Validation rule
  private locationRequiredRule = (v: any) => !!v || 'Delivery location is required';

  get showTryNewPoBanner(): boolean {
    return getFeatureVersion('purchaseOrder') === 'classic';
  }

  private switchPoToAtlas(): void {
    setFeatureVersion('purchaseOrder', 'atlas');
    this.$router.push(purchaseOrderSwitchTarget('purchaseOrderList'));
  }

  async created() {
    // Create debounced search function (300ms delay)
    this.debouncedSearch = this.debounce(this.applyFilterAndResetPage, 300);

    // Initialize filters from URL parameters
    this.initializeFromURLParams();

    const initialColumnOp = ++this.columnConfigOpVersion;
    const [, , , visibleColumns] = await Promise.all([
      this.fetchOrdersData(),
      this.fetchSuppliers(),
      this.fetchLocations(),
      fetchListColumnsConfig(),
    ]);
    if (this.columnConfigOpVersion === initialColumnOp) {
      this.visibleColumns = visibleColumns;
    }
  }

  public async onApplyColumns(next: string[]) {
    const previous = this.visibleColumns;
    const opId = ++this.columnConfigOpVersion;
    this.visibleColumns = next;
    try {
      await saveListColumnsConfig(next);
    } catch (error) {
      if (this.columnConfigOpVersion !== opId) {
        return;
      }
      this.visibleColumns = previous;
      EventBus.$emit('show-snackbar', {
        message: await extractApiErrorMessage(error, 'Failed to save column preferences'),
        color: 'error',
      });
    }
  }

  beforeDestroy() {
    // Clean up debounce timer
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  }

  private initializeFromURLParams() {
    // US-050: read the entire PO list filter/sort/page state from the
    // route query so back-nav from detail and reload both round-trip.
    const parsed = parsePoListQuery(this.$route.query as any);

    if (parsed.search !== undefined) { this.search = parsed.search; }
    if (parsed.searchField !== undefined) { this.searchField = parsed.searchField; }
    if (parsed.status !== undefined) { this.filters.status = parsed.status; }
    if (parsed.source !== undefined) { this.filters.source = parsed.source; }
    if (parsed.supplierId !== undefined) { this.filters.supplierId = parsed.supplierId; }
    if (parsed.locationId !== undefined) { this.filters.locationId = parsed.locationId; }
    if (parsed.orderDateFrom !== undefined) { this.filters.orderDateFrom = parsed.orderDateFrom; }
    if (parsed.orderDateTo !== undefined) { this.filters.orderDateTo = parsed.orderDateTo; }
    if (parsed.departureDateFrom !== undefined) { this.filters.departureDateFrom = parsed.departureDateFrom; }
    if (parsed.departureDateTo !== undefined) { this.filters.departureDateTo = parsed.departureDateTo; }
    if (parsed.expectedDeliveryDateFrom !== undefined) { this.filters.expectedDeliveryDateFrom = parsed.expectedDeliveryDateFrom; }
    if (parsed.expectedDeliveryDateTo !== undefined) { this.filters.expectedDeliveryDateTo = parsed.expectedDeliveryDateTo; }
    if (parsed.paymentStatus !== undefined) { this.filters.paymentStatus = parsed.paymentStatus; }
    if (parsed.overdue !== undefined) { this.filters.overdue = parsed.overdue; }
    if (parsed.page !== undefined) { this.options.page = parsed.page; }
    if (parsed.itemsPerPage !== undefined) { this.options.itemsPerPage = parsed.itemsPerPage; }
    if (parsed.sortBy !== undefined) { this.options.sortBy = [parsed.sortBy]; }
    if (parsed.sortDesc !== undefined) { this.options.sortDesc = [parsed.sortDesc]; }
  }

  private currentPoListState(): PoListQueryState {
    return {
      search: this.search,
      searchField: this.searchField,
      status: this.filters.status,
      source: this.filters.source,
      supplierId: this.filters.supplierId,
      locationId: this.filters.locationId,
      orderDateFrom: this.filters.orderDateFrom,
      orderDateTo: this.filters.orderDateTo,
      departureDateFrom: this.filters.departureDateFrom,
      departureDateTo: this.filters.departureDateTo,
      expectedDeliveryDateFrom: this.filters.expectedDeliveryDateFrom,
      expectedDeliveryDateTo: this.filters.expectedDeliveryDateTo,
      paymentStatus: this.filters.paymentStatus,
      overdue: this.filters.overdue,
      page: this.options.page,
      itemsPerPage: this.options.itemsPerPage,
      sortBy: this.options.sortBy[0] || PO_LIST_DEFAULTS.sortBy,
      sortDesc: this.options.sortDesc[0] !== undefined ? this.options.sortDesc[0] : PO_LIST_DEFAULTS.sortDesc,
    };
  }

  private syncFiltersToRoute() {
    // US-050: mirror state → URL so browser back, reload, bookmark, and
    // share-via-link all survive. App.vue's @Watch('$route') propagates
    // the new query to Shopify App Bridge's top-level URL for free.
    //
    // Guard against no-op replaces to avoid NavigationDuplicated errors.
    // Use `replace` (not `push`) so keystroke-level filter changes don't
    // spam the browser history stack.
    const newQuery = serializePoListState(this.currentPoListState());
    if (queryShallowEqual(newQuery, this.$route.query as any)) { return; }
    this.$router.replace({ query: newQuery });
  }

  private async fetchOrdersData() {
    await this.fetchOrders();
  }

  private async fetchOrders() {
    // US-050: mirror current state into the URL before every fetch so
    // every filter/pagination/sort change lands in the route query.
    this.syncFiltersToRoute();
    this.loading = true;
    try {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const orderBy = sortBy.length > 0 ? sortBy[0] : 'createdAt';
      const orderDir = sortDesc.length > 0 && sortDesc[0] ? 'desc' : 'asc';

      await this.purchaseOrdersCollection.fetch({
        page,
        limit: itemsPerPage,
        ...this.buildFilterParams(),
        sortBy: orderBy,
        sortDir: orderDir
      });

      this.purchaseOrders = this.purchaseOrdersCollection.items;
      this.totalPurchaseOrders = this.purchaseOrdersCollection.total;
    } catch (error) {
      this.$emit('error', 'Failed to load purchase orders');
      console.error('Error fetching purchase orders:', error);
    } finally {
      this.loading = false;
    }
  }

  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const suppliersCollection = new SupplierList();
      await suppliersCollection.fetch();
      this.suppliers = suppliersCollection.items;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      const locationsList = new StoreLocationList();
      await locationsList.fetch();
      this.locations = locationsList.items;
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      this.loadingLocations = false;
    }
  }

  get statusOptions() {
    return this.purchaseOrdersCollection.getStatusOptions();
  }

  get statusPresets() {
    return PO_STATUS_PRESETS;
  }

  get statusFilterLabel(): string {
    const preset = findPoStatusPreset(this.filters.status);
    if (preset) { return preset.text; }
    return normalizePoStatuses(this.filters.status).map(s => this.getStatusLabel(s)).join(', ');
  }

  get statusSelectionText(): string {
    const preset = findPoStatusPreset(this.filters.status);
    if (preset) { return preset.text; }
    const statuses = normalizePoStatuses(this.filters.status);
    return statuses.length === 1 ? this.getStatusLabel(statuses[0]) : `${statuses.length} statuses`;
  }

  applyStatusPreset(statuses: ReadonlyArray<string>) {
    this.filters.status = [...statuses];
    this.applyFilterAndResetPage();
  }

  get supplierOptions() {
    return this.suppliers;
  }

  get locationOptions() {
    return this.locations;
  }

  get sourceOptions() {
    return [
      { text: 'Manual', value: 'manual' },
      { text: 'Automated', value: 'automated' }
    ];
  }

  get searchFieldOptions() {
    return [
      { text: 'All fields', value: '' },
      ...PO_SEARCH_FIELD_VALUES.map(v => ({ text: PO_SEARCH_FIELD_LABELS[v], value: v })),
    ];
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.search ||
      this.searchField ||
      this.filters.status.length ||
      this.filters.source ||
      this.filters.supplierId ||
      this.filters.locationId ||
      this.filters.orderDateFrom ||
      this.filters.orderDateTo ||
      this.filters.departureDateFrom ||
      this.filters.departureDateTo ||
      this.filters.expectedDeliveryDateFrom ||
      this.filters.expectedDeliveryDateTo
    );
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.search) count++;
    if (this.searchField) count++;
    if (this.filters.status.length) count++;
    if (this.filters.source) count++;
    if (this.filters.supplierId) count++;
    if (this.filters.locationId) count++;
    if (this.filters.orderDateFrom || this.filters.orderDateTo) count++;
    if (this.filters.departureDateFrom || this.filters.departureDateTo) count++;
    if (this.filters.expectedDeliveryDateFrom || this.filters.expectedDeliveryDateTo) count++;
    return count;
  }

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.value === status);
    return option ? option.text : status;
  }

  getLocationName(locationId: number | string | null): string {
    if (!locationId) return '-';
    // Convert both to strings for comparison since API returns string IDs
    const location = this.locations.find(loc => String(loc.id) === String(locationId));
    return location ? location.name : '-';
  }

  applyFilterAndResetPage() {
    this.options.page = 1;
    this.fetchOrders();
  }

  clearAllFilters() {
    this.search = '';
    this.searchField = '';
    this.filters.status = [];
    this.filters.source = '';
    this.filters.paymentStatus = [];
    this.filters.overdue = false;
    this.filters.supplierId = null;
    this.filters.locationId = null;
    this.filters.orderDateFrom = '';
    this.filters.orderDateTo = '';
    this.filters.departureDateFrom = '';
    this.filters.departureDateTo = '';
    this.filters.expectedDeliveryDateFrom = '';
    this.filters.expectedDeliveryDateTo = '';
    this.applyFilterAndResetPage();
  }

  openCreatePODialog() {
    this.selectedSupplierId = null;
    this.createPODialogVisible = true;
    if (!this.suppliers || this.suppliers.length === 0) {
      this.fetchSuppliers();
    }
  }

  closeCreatePODialog() {
    this.createPODialogVisible = false;
    this.selectedSupplierId = null;
  }

  closeCreateForImportDialog() {
    this.showCreateForImportDialog = false;
    this.importPO = {
      supplierId: null,
      locationId: null,
      expectedDeliveryDate: '',
      notes: ''
    };
    (this.$refs.importForm as Vue & { reset: () => void }).reset();
  }

  async createPOForImport() {
    if (!(this.$refs.importForm as Vue & { validate: () => boolean }).validate()) {
      return;
    }

    this.creatingForImport = true;
    try {
      const response = await PurchaseOrderModel.createForImport({
        supplierId: this.importPO.supplierId || undefined,
        locationId: this.importPO.locationId || undefined,
        expectedDeliveryDate: this.importPO.expectedDeliveryDate || undefined,
        notes: this.importPO.notes || undefined
      });
      this.$router.push(`/purchase-orders/${response.data.id}?import=true`);
      EventBus.$emit('show-snackbar', {
        message: 'Purchase order created successfully',
        color: 'success'
      });

      this.closeCreateForImportDialog();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Failed to create purchase order: ${error.message}`,
        color: 'error'
      });
    } finally {
      this.creatingForImport = false;
    }
  }

  async exportPurchaseOrders() {
    this.exporting = true;

    try {
      const params = this.buildExportParams();
      const token = getIdentityToken();

      const response = await ax.get('/purchase_orders/export', {
        params,
        responseType: 'blob',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      const blobUrl = window.URL.createObjectURL(response.data);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `purchase_orders_${new Date().toISOString().split('T')[0]}.${this.exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      this.showExportDialog = false;

      EventBus.$emit('show-snackbar', {
        message: 'Export completed successfully.',
        color: 'success'
      });
    } catch (error: any) {
      console.error('Export error:', error);
      // Blob-aware: without this the backend's "Export would exceed the
      // maximum of N records" is lost and users only see a generic failure.
      EventBus.$emit('show-snackbar', {
        message: await extractApiErrorMessage(error, 'Export failed. Please try again.'),
        color: 'error'
      });
    } finally {
      this.exporting = false;
    }
  }

  /**
   * The filter half of the request, shared by fetchData() and
   * buildExportParams().
   *
   * These MUST be one function. The export dialog promises "Export
   * {{ totalPurchaseOrders }} POs matching current filters" using the count
   * fetchData() produced, so any filter sent by one and not the other makes
   * the export match a different set than the number the user was shown.
   * When the two were maintained by hand the export silently dropped
   * searchField and every date range, and a list narrowed to a few hundred
   * POs still exported thousands.
   */
  buildFilterParams() {
    const statuses = normalizePoStatuses(this.filters.status);

    return {
      search: this.search || undefined,
      searchField: this.searchField || undefined,
      status: statuses.length ? statuses.join(',') : undefined,
      source: this.filters.source || undefined,
      supplierId: this.filters.supplierId || undefined,
      locationId: this.filters.locationId || undefined,
      fromDate: this.filters.orderDateFrom || undefined,
      toDate: this.filters.orderDateTo || undefined,
      departureDateFrom: this.filters.departureDateFrom || undefined,
      departureDateTo: this.filters.departureDateTo || undefined,
      expectedDeliveryDateFrom: this.filters.expectedDeliveryDateFrom || undefined,
      expectedDeliveryDateTo: this.filters.expectedDeliveryDateTo || undefined,
      paymentStatus: this.filters.paymentStatus.length ? this.filters.paymentStatus.join(',') : undefined,
      overdue: this.filters.overdue || undefined
    };
  }

  buildExportParams() {
    const { sortBy, sortDesc } = this.options;

    return {
      ...this.buildFilterParams(),
      format: this.exportFormat,
      includeFinancials: this.exportIncludeFinancials,
      sortBy: sortBy.length > 0 ? sortBy[0] : 'createdAt',
      sortDir: sortDesc.length > 0 && sortDesc[0] ? 'DESC' : 'ASC'
    };
  }

  navigateToCreatePO() {
    if (this.selectedSupplierId) {
      this.$router.push(`/suppliers/${this.selectedSupplierId}/purchase-orders/create`);
      this.closeCreatePODialog();
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'draft':
        return '#9E9E9E';
      case 'confirmed':
        return '#9C27B0';
      case 'partial_received':
        return '#FF9800';
      case 'received':
        return '#4CAF50';
      case 'completed':
        return '#009688';  // Teal
      case 'canceled':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'draft':
        return 'mdi-pencil';
      case 'confirmed':
        return 'mdi-check-circle';
      case 'partial_received':
        return 'mdi-package-variant';
      case 'received':
        return 'mdi-package-variant-closed';
      case 'completed':
        return 'mdi-check-circle-outline';
      case 'canceled':
        return 'mdi-close-circle';
      default:
        return 'mdi-information';
    }
  }

  formatStatus(status: string): string {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'confirmed':
        return 'Confirmed';
      case 'partial_received':
        return 'Partially Received';
      case 'received':
        return 'Received';
      case 'completed':
        return 'Completed';
      case 'canceled':
        return 'Canceled';
      default:
        return status;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return dateFormat(new Date(dateString), 'mmm d, yyyy');
  }

  formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrency(value);
  }

  get storeCurrency(): string {
    return CurrencyHelper.getStoreCurrency();
  }

  isForeignPO(po: any): boolean {
    return !!po.currencyCode && po.currencyCode !== this.storeCurrency;
  }

  formatPOCurrency(po: any): string {
    const currency = po.currencyCode || this.storeCurrency;
    return CurrencyHelper.formatCurrencyWithCode(po.totalAmount, currency);
  }

  // US-084: format an arbitrary extra.financial.* value in the PO's currency,
  // for the financial detail columns (Subtotal, Item Tax, Shipping, etc.).
  public formatPOMoney(po: any, value: number | undefined | null): string {
    if (value === undefined || value === null) {
      return '-';
    }
    const currency = po.currencyCode || this.storeCurrency;
    return CurrencyHelper.formatCurrencyWithCode(value, currency);
  }

  get canManagePurchaseOrders(): boolean {
    const validateValues = [
      {
        code: 'operational_mode',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  /**
   * Debounce utility function
   * Creates a debounced version of a function that delays execution
   * until after the specified wait time has elapsed since the last call
   */
  private debounce(func: Function, wait: number) {
    let timeout: number | null = null;

    const debounced = (...args: any[]) => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };

    // Add cancel method to clear pending execution
    debounced.cancel = () => {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    return debounced;
  }
}
